import { escapeRegex, has } from "./utils";

/**
 * An NGSS definition for a dataset
 * @typedef {Object} NGSS
 * @property {string} title - The name of the Next Gen Sci. Standard
 * @property {number[]} gradeRange - The range of grades for which this
 *   definition is applicable. The first element is the lower bound of the grade
 *   range, the second element is the upper bound
 */

function compareBy(sortOrder) {
  function name(a, b) {
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  }

  function nameReverse(a, b) {
    return name(a, b) * -1;
  }

  function dateAdded(a, b) {
    if (a.dateAdded < b.dateAdded) return -1;
    if (a.dateAdded > b.dateAdded) return 1;
    return name(a, b);
  }

  function dateAddedReverse(a, b) {
    return dateAdded(a, b) * -1;
  }

  switch (sortOrder) {
    case "dateAdded-dsc":
      return dateAddedReverse;

    case "dateAdded-asc":
      return dateAdded;

    case "name-dsc":
      return nameReverse;

    default:
      return name;
  }
}

/**
 * Send an XHR to window.SOS_CATALOG_URL to load the data
 *
 * If the request is successful, cb is called with the JavaScript object created
 * by parsing the response text. If the request fails, an error is logged to the
 * console, and cb is called with no arguments.
 *
 * @param {Function} cb called when the XHR completes
 */
function fetchCatalog(cb) {
  var xhr = new XMLHttpRequest();

  xhr.onreadystatechange = function () {
    // Do nothing until the request is complete
    if (xhr.readyState !== 4) return;

    if (xhr.status < 200 || xhr.status >= 300) {
      // FIXME: We also need some way to inform the user that this failed.

      console.error("Failed to load " + window.SOS_CATALOG_URL);
      console.error(xhr.status, xhr.responseText);
      cb("We couldn’t load the catalog.");
    } else {
      try {
        cb(null, JSON.parse(xhr.responseText));
      } catch (err) {
        cb(
          "There’s an error in our catalog data and we are unable to read it.",
          null
        );
      }
    }
  };

  xhr.open("GET", window.SOS_CATALOG_URL);
  xhr.send();
}

function filterBy(data) {
  /**
   * Returns `true` if `range` is match for the range defined by `[lower, upper]`.
   *
   * A grade range is considere a match to the target range if it completely
   * contains the grades defined in the target. If the target range is 3–6, then
   * K–12 is a match because every grade level in the target range is
   * represented; 3–4 is not a match because 5th and 6th grades would be left
   * out.
   *
   * If `lower` is omitted—i.e. if it is null, undefined, or infinite—the lower
   * bound is not checked. If `upper` is omitted, the upper bound is not
   * checked.
   *
   * @param {number[]} range - The grade range to test. The first element is the
   *   lower bound, the second element is the upper bound
   * @param {number=} lower - The lower bound of the target grade range
   * @param {number=} upper - The upper bound of the target grade range
   * @returns `true` if range completely contains [lower, upper]
   */
  function matchGradeRange(range, lower, upper) {
    if (
      isFinite(lower) &&
      lower !== null &&
      (range[0] > lower || range[1] < lower)
    ) {
      return false;
    }

    if (
      isFinite(upper) &&
      upper !== null &&
      (range[1] < upper || range[0] > upper)
    ) {
      return false;
    }

    return true;
  }

  /**
   * Returns `true` if `ngss` matches `title`, `minGrade`, and `maxGrade`.
   *
   * @param {NGSS} ngss - An NGSS definition to test
   * @param {string} title - The title of the NGSS standard to match
   * @param {number=} minGrade - The lower bound of the target grade range
   * @param {number=} maxGrade - The upper bound of the target grade range
   * @returns `true` if `ngss` matches the `title` and grade range
   */
  function matchNGSS(ngss, title, minGrade, maxGrade) {
    // If the title doesn't match, this isn't a match
    if (ngss.title !== title) return false;

    return matchGradeRange(ngss.gradeRange, minGrade, maxGrade);
  }

  /**
   * Returns true if `dataset` matches the selected filters.
   *
   * This function is the return object of the `datasetFilters` factory. It is
   * intended for use as the predicte of `Array.prototype.filter` to select
   * datasets that match the currently selected filters.
   *
   * @param {Object} dataset
   * @param {boolean} dataset.isRealtime true if the dataset is a realtime
   *   dataset
   * @param {boolean} dataset.hasAudio true if the dataset has audio
   * @param {boolean} dataset.isNarratedMovie true if the dataset is a movie
   *   with audio narration
   * @param {boolean} dataset.isSos true if the dataset is available on SOS
   * @param {boolean} dataset.isSosx true if the dataset is available on SOSx
   * @param {string[]} dataset.themes Array of themes applied to this dataset
   * @param {string[]} dataset.keywords Array of keywords applied to this
   *   dataset
   * @param {Object} dataset.ngss Collection of NGSS standards valid for this
   *   dataset
   * @param {NGSS[]} dataset.ngss.crossCuttingConcepts Array of cross-cutting
   *   concepts for this dataset
   * @param {NGSS[]} dataset.ngss.disciplinaryCoreIdeas Array of disciplinary
   *   core ideas for this dataset
   * @param {NGSS[]} dataset.ngss.sciEngPractices Array of science & engineering
   *   practices for this dataset
   * @param {number[]} dataset.gradeRange The range of grades for which this
   *   dataset is suitable according to the available NGSS standards
   * @param {Object} dataset.categories maps categories to an array of
   *   subcategories that apply to this dataset
   *
   * @returns true if all of the dataset's properties match the applied filters
   */
  function predicate(dataset) {
    var category;

    // Short-circuit for no filters applied
    if (Object.keys(data).length < 1) return true;

    if (data.realtime && !dataset.isRealtime) return false;
    if (data.audioDescription && !dataset.hasAudio) return false;
    if (data.narratedMovie && !dataset.isNarratedMovie) return false;
    if (data.platform === "sos" && !dataset.isSos) return false;
    if (data.platform === "sosx" && !dataset.isSosx) return false;
    if (data.theme && dataset.themes.indexOf(data.theme) < 0) return false;

    if (data.keyword && dataset.keywords.indexOf(data.keyword) < 0) {
      return false;
    }

    if (data.year) {
      if (!(dataset.startDate || dataset.endDate)) return false;

      var year = +data.year;

      if (isNaN(year)) return false;

      var start = dataset.startDate
        ? new Date(dataset.startDate).getFullYear()
        : new Date(dataset.endDate).getFullYear();

      var end = dataset.endDate
        ? new Date(dataset.endDate).getFullYear()
        : start;

      if (year < start || year > end || (isNaN(start) && isNaN(end))) {
        return false;
      }
    }

    // FIXME: We need to use parseInt here because +"" === 0 in JavaScript's
    // "type" system. Unfortunately, parseInt("1monkey2") === 1, which I guess
    // is ok, but ideally we should ignore or warn users about invalid numeric
    // output.
    var minGrade = parseInt(data.minGrade);
    var maxGrade = parseInt(data.maxGrade);

    // If either min or max grade is specified and this dataset has no grade
    // range, exclude it.
    if (
      [minGrade, maxGrade].some(function (n) {
        return !isNaN(n);
      }) &&
      !has(dataset, "gradeRange")
    ) {
      return false;
    }

    if (!matchGradeRange(dataset.gradeRange, minGrade, maxGrade)) return false;

    var ngssMatch;

    if (has(data, "ccc") && data.ccc !== "") {
      ngssMatch = dataset.ngss.crossCuttingConcepts.reduce(function (
        match,
        concept
      ) {
        return match || matchNGSS(concept, data.ccc, minGrade, maxGrade);
      },
      false);

      if (!ngssMatch) return false;
    }

    if (has(data, "dci") && data.dci !== "") {
      ngssMatch = dataset.ngss.disciplinaryCoreIdeas.reduce(function (
        match,
        idea
      ) {
        return match || matchNGSS(idea, data.dci, minGrade, maxGrade);
      },
      false);

      if (!ngssMatch) return false;
    }

    if (has(data, "sep") && data.sep !== "") {
      ngssMatch = dataset.ngss.sciEngPractices.reduce(function (
        match,
        practice
      ) {
        return match || matchNGSS(practice, data.sep, minGrade, maxGrade);
      },
      false);

      if (!ngssMatch) return false;
    }

    // Move full text search towards the end, since it's bound to be more
    // expensive than other filters. We can't put it last because our category
    // checking logic relies on being the last check.
    if (fullTextQuery && !fullTextQuery.test(dataset.fullText)) return false;

    if (data.category) {
      if (!dataset.categories[data.category]) return false;

      // If we're filtering by category and subcategory, make sure that this
      // subcategory exists for this dataset in this category. "Live
      // Programs", for example, are subcategories of many categories.
      if (
        data.subcategory &&
        dataset.categories[data.category].indexOf(data.subcategory) < 0
      ) {
        return false;
      }
    } else if (data.subcategory) {
      // If we allow any category, but are looking for a specific subcategory,
      // e.g. all Live Programs.

      // Old school object iteration because using map or forEach makes
      // returning from the predicate function trickier.
      for (category in dataset.categories) {
        if (
          !Object.prototype.hasOwnProperty.call(dataset.categories, category)
        ) {
          continue;
        }

        // Since this is our very last check, if we find a matching
        // subcategory, we know this dataset matches our filters, so we can
        // return true.
        if (dataset.categories[category].indexOf(data.subcategory) > -1) {
          return true;
        }
      }

      // At this point we know this dataset doesn't match the requested
      // subcategory, so we exclude it.
      return false;
    }

    // If all the above checks pass, we must be able to include this dataset
    return true;
  }

  // Always search with lowercase.
  var fullTextQuery = data.fullText ? new RegExp(`\\b${escapeRegex(data.fullText.toLowerCase())}`) : "";

  return predicate;
}

module.exports = {
  compareBy: compareBy,
  fetchCatalog: fetchCatalog,
  filterBy: filterBy,
};
